// Customizable Area Start
import React from "react";
import { CSSObject } from "@emotion/react";
import { Box, Typography, styled, IconButton, Grid } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Keyboard } from "swiper/modules";
import { Swiper as SwiperType } from "swiper/types";
import ChatRenderController, {
  Props,
  THUMB_SWIPER_BREAKPOINTS,
  AttachmentMeta,
} from "./ChatRenderController.web";
import CustomChatBubble from "../../../components/src/CustomChatBubble.web";
import ConditionalRenderer from "../../../components/src/ConditionalRenderer.web";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import AudioMessage from "../../../components/src/AudioMessage.web";
import VideoMessage from "../../../components/src/VideoMessage.web";
import FileMessage from "../../../components/src/FileMessage.web";
import CustomCallBubble from "../../../components/src/CustomCallBubble.web";
import MessageBubbleComponent from "../../../components/src/MessageBubbleComponent.web";
import {
  capitalizeFirstLetter,
  formatCallTimer,
} from "../../../components/src/utils";
// Customizable Area End

export default class ChatRender extends ChatRenderController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  // Customizable Area End

  render() {
    // Customizable Area Start
    const { chatDetails, loggedInUserId } = this.props;
    const { type, sentAt, readAt } = chatDetails;
    const messageDate = this.getLocalDate(sentAt);
    const messageTime = this.getLocalTime(sentAt);

    if (chatDetails.category === "call") {
      const {
        id,
        status,
        conversationId,
        callInitiator,
        data: {
          entities: {
            on: {
              entity: { duration },
            },
          },
        },
      } = chatDetails;
      const isOwnCall = loggedInUserId === callInitiator.uid;
      const iconName = this.getCallLogIcon(chatDetails, isOwnCall);
      const callType = type === "audio" ? "Voice call" : "Video call";
      const isMissedCall = isOwnCall
        ? false
        : ["busy", "unanswered"].includes(status);
      const subTitle =
        status === "ended"
          ? `Call Duration ${formatCallTimer(duration)}`
          : capitalizeFirstLetter(status);

      return (
        <CustomCallBubble
          id={id}
          callType={iconName}
          isMissedCall={isMissedCall}
          chatId={conversationId}
          senderId={callInitiator.uid}
          subTitle={subTitle}
          date={messageDate}
          time={messageTime}
          loggedInUserId={loggedInUserId}
          title={callType}
        />
      );
    }

    const { isImagePreviewOpen, currentImageIndex, thumbsSwiper } = this.state;
    const { id, conversationId, sender, attachments, text } = this.parseMessage(
      chatDetails
    );
    const isOwnMessage = loggedInUserId === sender?.uid;
    const images = attachments.filter((attachment) =>
      attachment.mimeType.startsWith("image")
    );
    const audio = attachments.filter((attachment) =>
      attachment.mimeType.startsWith("audio")
    );
    const video = attachments.filter((attachment) =>
      attachment.mimeType.startsWith("video")
    );
    const file = attachments.filter((attachment) =>
      attachment.mimeType.startsWith("application")
    );

    const totalImages = images.length;
    const imagesToShow = totalImages > 3 ? 3 : totalImages;
    const isSeen = !!readAt;

    return (
      <>
        <MainWrapper className={isOwnMessage ? "own_message_wrapper" : ""}>
          <ConditionalRenderer isVisible={!!text}>
            <CustomChatBubble
              id={id}
              isSeen={isSeen}
              message={text || ""}
              senderId={sender?.uid || ""}
              chatId={conversationId}
              data-test-id="chat-bubble"
              loggedInUserId={loggedInUserId}
              date={messageDate}
              time={messageTime}
            />
          </ConditionalRenderer>

          {images.length > 0 && (
            <MessageBubbleComponent
              senderId={sender?.uid || ""}
              loggedInUserId={loggedInUserId}
              time={messageTime}
              date={messageDate}
              isSeen={isSeen}
            >
              <ImagesWrapper>
                <Container>
                  <Box
                    flex={2}
                    data-test-id="first-attachment"
                    onClick={() => this.openImagePreview(0)}
                  >
                    <MainImage src={images[0].url} alt="Main" />
                  </Box>
                  {totalImages > 1 && (
                    <StackedImages>
                      {images
                        .slice(1, imagesToShow)
                        .map((image: AttachmentMeta, index: number) => (
                          <StackImageWrapper
                            key={image.name}
                            data-test-id={`attachment-${index}`}
                            onClick={() => this.openImagePreview(index + 1)}
                          >
                            <StackedImage
                              src={image.url}
                              alt={`Image ${index + 1}`}
                            />
                            {totalImages > 3 && index === 1 && (
                              <Overlay>
                                <Typography variant="body1">
                                  +{totalImages - 3}
                                </Typography>
                              </Overlay>
                            )}
                          </StackImageWrapper>
                        ))}
                    </StackedImages>
                  )}
                </Container>
              </ImagesWrapper>
            </MessageBubbleComponent>
          )}

          {isImagePreviewOpen && (
            <FullscreenOverlay data-test-id="preview-screen">
              <Grid container justifyContent="center">
                <Grid item xs={12} sm={10} md={8} xl={7}>
                  <ImageGalleryWrapper>
                    <ImagesSwiper
                      spaceBetween={10}
                      data-test-id="images-swiper"
                      ref={this.swiperRef}
                      keyboard={{ enabled: true, onlyInViewport: true }}
                      initialSlide={currentImageIndex}
                      thumbs={{ swiper: thumbsSwiper || null }}
                      modules={[Navigation, Thumbs, Keyboard]}
                      onSlideChange={(swiper: SwiperType) =>
                        this.handleSlideChange(swiper.realIndex)
                      }
                    >
                      {images.map((image: AttachmentMeta, index: number) => (
                        <SwiperSlide key={`Main ${index}`}>
                          <img src={image.url} alt="Attachment" />
                        </SwiperSlide>
                      ))}
                    </ImagesSwiper>

                    <CustomArrowButtonLeft
                      data-test-id="left-nav-button"
                      onClick={this.goToPrevSlide}
                    >
                      <ArrowBackIosNewRoundedIcon />
                    </CustomArrowButtonLeft>
                    <CustomArrowButtonRight
                      data-test-id="right-nav-button"
                      onClick={this.goToNextSlide}
                    >
                      <ArrowForwardIosRoundedIcon />
                    </CustomArrowButtonRight>

                    <ThumbsSwiper
                      slidesPerView={5}
                      spaceBetween={15}
                      watchSlidesProgress
                      data-test-id="thumbnail-swiper"
                      breakpoints={THUMB_SWIPER_BREAKPOINTS}
                      onSwiper={this.handleOnSwiperChange}
                      modules={[Navigation, Thumbs]}
                    >
                      {images.map((image: AttachmentMeta, index: number) => (
                        <SwiperSlide key={`Thumb ${index}`}>
                          <ThumbImageWrapper>
                            <img
                              src={image.url}
                              alt={`Thumbnail ${index + 1}`}
                            />
                            {index === currentImageIndex && (
                              <ActiveThumbnailOverlay />
                            )}
                          </ThumbImageWrapper>
                        </SwiperSlide>
                      ))}
                    </ThumbsSwiper>
                  </ImageGalleryWrapper>
                </Grid>
              </Grid>
              <CloseButton
                data-test-id="preview-close-btn"
                onClick={this.closeImagePreview}
              >
                <CloseRoundedIcon />
              </CloseButton>
            </FullscreenOverlay>
          )}

          {audio.length > 0 && (
            <AudioMessage
              audioUrl={audio[0].url}
              time={messageTime}
              isSeen={isSeen}
              senderId={sender?.uid || ""}
              data-test-id="chat-bubble"
              loggedInUserId={loggedInUserId}
              date={messageDate}
            />
          )}

          {video.length > 0 && (
            <VideoMessage
              videoUrl={video[0].url}
              time={messageTime}
              isSeen={isSeen}
              senderId={sender?.uid || ""}
              data-test-id="chat-bubble"
              loggedInUserId={loggedInUserId}
              date={messageDate}
            />
          )}

          {file.length > 0 && (
            <FileMessage
              fileUrl={file[0].url}
              fileName={file[0].name}
              fileExtension={file[0].extension}
              time={messageTime}
              isSeen={isSeen}
              senderId={sender?.uid || ""}
              data-test-id="chat-bubble"
              loggedInUserId={loggedInUserId}
              date={messageDate}
            />
          )}
        </MainWrapper>
      </>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const MainWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  marginBottom: "15px",
  padding: "0px 10px",
  "&.own_message_wrapper": {
    alignItems: "flex-end",
  },
});

const ImagesWrapper = styled(Box)({
  marginTop: "5px",
  maxWidth: "420px",
  width: "100%",
  marginBottom: "4px",
  "@media (max-width: 850px)": {
    maxWidth: "90%",
  },
});

const Container = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "5px",
  width: "100%",
  height: "35vh",
  maxHeight: "280px",
  overflow: "hidden",
  justifyContent: "flex-end",
  "@media (max-width: 850px)": {
    height: "26vh",
  },
});

const MainImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "6px",
});

const StackedImages = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  flex: 1,
  height: "100%",
});

const StackImageWrapper = styled(Box)({
  position: "relative",
  flex: 1,
  minHeight: 0,
  "&:only-child": {
    flex: "unset",
    height: "50%",
  },
});

const StackedImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "6px",
});

const Overlay = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundColor: "#65278480",
  color: "#fff",
  padding: "5px 10px",
  borderRadius: "5px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& p": {
    fontSize: "1.875rem",
    fontWeight: 500,
    letterSpacing: "-0.15px",
  },
});

const FullscreenOverlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(15, 23, 42, 0.95)",
  zIndex: 1000,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 0,
  margin: 0,
  boxSizing: "border-box",
});

const ImageGalleryWrapper = styled(Box)({
  width: "80%",
  height: "80vh",
  boxSizing: "border-box",
  margin: "0 auto",
  position: "relative",
  "@media (max-width: 768px)": {
    height: "50vh",
  },
});

const ImagesSwiper = styled(Swiper)({
  height: "80%",
  ".swiper-slide img": {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "fill",
    borderRadius: "2px",
  },
});

const ThumbsSwiper = styled(Swiper)({
  width: "70%",
  boxSizing: "border-box",
  padding: "20px 0",
  "& .swiper-slide": {
    margin: "0 auto !important",
    img: {
      display: "block",
      width: "72px",
      height: "72px",
      objectFit: "cover",
      borderRadius: "2px",
      margin: "0 auto",
    },
  },
  "@media (max-width: 414px)": {
    width: "100%",
  },
  "@media (max-width: 768px)": {
    width: "90%",
  },
  "@media (max-width: 1024px)": {
    width: "80%",
  },
});

const ThumbImageWrapper = styled(Box)({
  position: "relative",
  height: "72px",
  width: "72px",
  margin: "0 auto",
  cursor: "pointer",
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  top: 25,
  right: 25,
  border: "none",
  color: "#fff",
  padding: "8px",
  cursor: "pointer",
  zIndex: 1001,
  "&:hover": {
    backgroundColor: "rgba(255,255,255, 0.1)",
  },
});

const arrowStyle: CSSObject = {
  position: "absolute",
  top: "40%",
  transform: "translateY(-50%)",
  zIndex: 1001,
  color: "#fff",
  width: "48px",
  height: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "30px",
  },
  "&:hover": {
    "& svg": {
      fontSize: "32px",
    },
  },
};

const CustomArrowButtonLeft = styled(IconButton)(arrowStyle, {
  left: "-60px",
  "@media (max-width: 768px)": {
    left: "-45px",
  },
});

const CustomArrowButtonRight = styled(IconButton)(arrowStyle, {
  right: "-60px",
  "@media (max-width: 768px)": {
    right: "-45px",
  },
});

const ActiveThumbnailOverlay = styled(Box)({
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  position: "absolute",
  background: "rgba(255, 255, 255, 0.4)",
  borderRadius: "2px",
  pointerEvents: "none",
});
// Customizable Area End
